'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import { Checkbox } from '@curology/ui-components-web/react/Checkbox';
import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { MaskedInput } from '@curology/ui-components-web/react/MaskedInput';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useEffect, useState } from 'react';

import { useNavigation } from '@/components/flow/hooks/useNavigation';
import LegalLinks from '@/components/legalLinks';
import { LegalModal } from '@/components/modules/legalModal';
import { BRAND_IDS } from '@/consts/brands';
import { DATA_KEYS } from '@/consts/dataKeys';
import { TreatmentCategoryIds } from '@/consts/treatmentCategories';
import { useRouter } from '@/hooks/useRouter';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { PhoneNumberVerificationService } from '@/services/phoneNumberVerificationService/client';
import { getVisit } from '@/services/visitsService/client';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { trackEvent, AnalyticsEvent } from '@/utils/analytics';

import { VIEW_TYPES } from './constants/constants';
import { type InputState } from './types/types';
import {
  getHumanReadableBrandName,
  isFourDigitVerificationCode,
  phoneNumberIsValid,
} from './utils/utils';
import { useDataProvider } from '../../components/DataProvider/useDataProvider';
import { useFlowContext } from '../../hooks/useFlowContext';
import { isUnder18 } from '../legal-name/utils/utils';

export const SmsVerification = () => {
  const {
    isGetTextsCheckboxChecked,
    patient,
    isPhoneNumberVerificationRequired,
  } = useDataProvider(
    DATA_KEYS.isGetTextsCheckboxChecked,
    DATA_KEYS.patient,
    DATA_KEYS.isPhoneNumberVerificationRequired,
  );

  const getTextsCheckboxChecked = isGetTextsCheckboxChecked?.data
    ? isGetTextsCheckboxChecked.data.sms_marketing_enabled &&
      isGetTextsCheckboxChecked.data.sms_transactional_enabled
    : false;

  const patientPhoneNumber = patient?.phone || '';

  const { visit } = useVisitContext();
  const { onNext } = useNavigation();
  const { treatmentCategory, brand } = useFlowContext();
  const brandId = BRAND_IDS[brand];
  const humanReadableBrandName = getHumanReadableBrandName(brand);

  const [view, setView] = useState(VIEW_TYPES.phoneNumberEntry);

  const [phoneNumber, setPhoneNumber] = useState(patientPhoneNumber);

  const [phoneNumberInputState, setPhoneNumberInputState] =
    useState<InputState>();

  const [getTexts, setGetTexts] = useState(getTextsCheckboxChecked);

  const [
    phoneNumberSubmitButtonIsDisabled,
    setPhoneNumberSubmitButtonIsDisabled,
  ] = useState(!phoneNumberIsValid(patientPhoneNumber));

  const [verificationCode, setVerificationCode] = useState('');

  const [verificationCodeInputState, setVerificationCodeInputState] =
    useState<InputState>();

  const [
    verificationCodeInputAndButtonsAreDisabled,
    setVerificationCodeInputAndButtonsAreDisabled,
  ] = useState(false);

  const router = useRouter();
  const { handleIfSessionExpired } = useSessionExpired(brand);

  // Why is this here?
  // In the new Polly SUF, this page is right after the Legal Name page with the DOB input. Because of how Next.js caches data, there are cases where a user can enter a valid DOB over 18 from the Legal Page page, be directed to this page, click the browser back button, and then enter a DOB under 18, and still see this page, which is a big no-no.
  // The following check pulls a fresh copy of the visit object, and re-checks whether the DOB is under 18 or not, and if it is, it refreshes the router, which will lead the user to the correct page and away from this one.
  // This is hacky, and we'd prefer a better solution, but there was no time for the Polly launch. Apologies to any engineers in the future that need to resolve this (my advice is to redo the SUF without Next.js).
  useEffect(() => {
    if (visit?.uuid) {
      const getCurrentVisit = async (uuid: string) => {
        const visit = await getVisit({ uuid });

        if (!visit?.user?.dob || isUnder18(visit.user.dob, false)) {
          router.refresh();
        }
      };

      getCurrentVisit(visit.uuid);
    }
  }, [visit, router]);

  return (
    <div className="cur-grow cur-m-auto cur-max-w-[407px] cur-px-4 cur-py-8 cur-text-subtle data-hj-suppress">
      {view === VIEW_TYPES.phoneNumberEntry && (
        <>
          <Typography className="cur-mb-4 cur-text-primary" size="h2">
            What’s your phone number?
          </Typography>
          <Typography className="cur-leading-6 cur-my-4 cur-whitespace-pre-line">
            {isPhoneNumberVerificationRequired
              ? `We’ll text you a one-time verification code to make sure you’re really you.`
              : 'Enter your phone number to receive text updates including order confirmations, shipment updates, and messages from your provider.'}
          </Typography>
          <div className="cur-my-4">
            <MaskedInput
              helperText={
                phoneNumberInputState === 'critical'
                  ? 'Please enter a valid phone number with 10 digits.'
                  : ''
              }
              label="Phone number"
              mask={[
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
                '-',
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
                '-',
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
              ]}
              onBlur={() => {
                setPhoneNumberInputState(
                  !phoneNumberIsValid(phoneNumber) ? 'critical' : undefined,
                );
              }}
              onChange={(event) => {
                const value = event.target.value || '';

                setPhoneNumber(value);

                setPhoneNumberSubmitButtonIsDisabled(
                  !phoneNumberIsValid(value),
                );
              }}
              onFocus={() => setPhoneNumberInputState(undefined)}
              placeholder="123-456-7890"
              state={phoneNumberInputState}
              value={phoneNumber}
            />
          </div>
          <hr className="cur-bg-surface-secondary cur-border-0 cur-h-[1px] cur-my-4" />
          <div className="cur-my-4">
            <Checkbox
              checked={getTexts}
              id="get-texts"
              label={`Get texts about your account, shipments, and ${humanReadableBrandName} news`}
              onChange={() => setGetTexts(!getTexts)}
              size="sm"
              tabIndex={0}
            />
          </div>
          <Highlight className="cur-leading-6 cur-my-4" size="sm">
            By checking the box above, I agree to receive transactional and/or
            marketing texts from {humanReadableBrandName} at the number
            provided. Opting in is always optional. Message frequency varies.
            Reply HELP for help, or STOP to cancel. Messages may be automated.
            Message and data rates may apply.
          </Highlight>
          <div className="cur-mb-4 cur-mt-6">
            <Button
              className="cur-w-full"
              disabled={phoneNumberSubmitButtonIsDisabled}
              onClick={async () => {
                setPhoneNumberSubmitButtonIsDisabled(true);

                try {
                  await Promise.all([
                    PhoneNumberVerificationService.updatePhoneNumber(
                      brand,
                      phoneNumber,
                    ),
                    PhoneNumberVerificationService.postSmsPreferences(
                      brand,
                      getTexts,
                    ),
                  ]);

                  if (getTexts) {
                    await PhoneNumberVerificationService.postSmsMarketingDoubleOptIn(
                      brand,
                      patient?.email,
                    );
                  } else {
                    await PhoneNumberVerificationService.postUnsubscribe(
                      brand,
                      patient?.email,
                    );
                  }

                  if (isPhoneNumberVerificationRequired) {
                    await PhoneNumberVerificationService.postPhoneNumberToTextConfirmationCode(
                      brand,
                      phoneNumber,
                    );

                    setView(VIEW_TYPES.verificationCodeEntry);
                    setPhoneNumberSubmitButtonIsDisabled(false);
                  } else {
                    trackEvent(AnalyticsEvent.SMS_VERIFICATION_PAGE_COMPLETED, {
                      brandId,
                      treatmentCategory: treatmentCategory!,
                      treatmentCategoryId:
                        TreatmentCategoryIds[treatmentCategory!],
                      userId: patient?.id,
                    });

                    onNext();
                  }
                } catch (e: any) {
                  if (handleIfSessionExpired(e.response?.status)) return;
                  setPhoneNumberSubmitButtonIsDisabled(false);
                }
              }}
            >
              Next
            </Button>
          </div>
        </>
      )}
      {view === VIEW_TYPES.verificationCodeEntry && (
        <>
          <Typography className="cur-mb-4 cur-text-primary" size="h2">
            Verify your phone number
          </Typography>
          <Typography className="cur-leading-6 cur-my-4 cur-whitespace-pre-line">
            We just texted you! Enter the code you received below.
          </Typography>
          <div className="cur-mb-6 cur-mt-4">
            <TextInput
              disabled={verificationCodeInputAndButtonsAreDisabled}
              helperText={
                verificationCodeInputState === 'critical'
                  ? 'Please enter a valid four-digit verification code.'
                  : `You'll receive an SMS to your phone.`
              }
              label="Verification code"
              onBlur={() => {
                if (!isFourDigitVerificationCode(verificationCode)) {
                  setVerificationCodeInputState('critical');
                }
              }}
              onChange={async (event) => {
                const value = event.target.value || '';

                setVerificationCode(value);
                setVerificationCodeInputState(undefined);

                if (isFourDigitVerificationCode(value)) {
                  setVerificationCodeInputAndButtonsAreDisabled(true);

                  try {
                    await PhoneNumberVerificationService.postConfirmationCode(
                      brand,
                      value,
                    );

                    trackEvent(AnalyticsEvent.SMS_VERIFICATION_PAGE_COMPLETED, {
                      brandId,
                      treatmentCategory: treatmentCategory!,
                      treatmentCategoryId:
                        TreatmentCategoryIds[treatmentCategory!],
                      userId: patient?.id,
                    });

                    onNext();
                  } catch (exception: any) {
                    if (handleIfSessionExpired(exception.response?.status))
                      return;
                    setVerificationCodeInputState('critical');
                    setVerificationCodeInputAndButtonsAreDisabled(false);
                  }
                }
              }}
              onFocus={() => setVerificationCodeInputState(undefined)}
              state={verificationCodeInputState}
              value={verificationCode}
            />
          </div>
          <div className="cur-mb-4 cur-mt-6">
            <Button
              className="cur-w-full"
              disabled={verificationCodeInputAndButtonsAreDisabled}
              onClick={() => {
                setVerificationCodeInputAndButtonsAreDisabled(true);

                onNext();
              }}
            >
              Skip
            </Button>
          </div>
          <div className="cur-my-4">
            <Button
              buttonType="text"
              className="cur-text-primary cur-w-full"
              disabled={verificationCodeInputAndButtonsAreDisabled}
              onClick={() => {
                setView(VIEW_TYPES.phoneNumberEntry);
                setVerificationCodeInputState(undefined);
              }}
            >
              Back
            </Button>
          </div>
        </>
      )}
      <div className="cur-text-center cur-gap-y-2 cur-mt-4">
        <LegalLinks.TermsOfService brand={brand} />
        {'  |  '}
        <LegalLinks.PrivacyPolicy brand={brand} />
      </div>
      <LegalModal />
    </div>
  );
};
